import { Temporal } from "temporal-polyfill";
import {
  PsvReviewItemKind,
  PsvReviewOutcome,
  PsvReviewStatus,
} from "@api/shared-runtime/psv-review";
import {
  formatCredentialTimestamp,
  formatDate,
} from "../../../lib/format-date";
import { CheckOutcome } from "../check-outcome";
import { findStateName } from "../state-names";
import type { ItemSummary, ReportItem, RunReport } from "../types";

/** Report sections in display order. `family` is the server's report family
 * value; it is an API-only enum, so it is compared as its string value. */
export const ITEM_FAMILY_SECTIONS: { family: string; label: string }[] = [
  { family: "state-license", label: "State licenses" },
  { family: "identity", label: "Identity" },
  { family: "dea", label: "DEA registrations" },
  { family: "medicare-participation", label: "Medicare participation" },
  { family: "exclusion", label: "Exclusions" },
  { family: "sanctions", label: "Sanctions" },
  { family: "other", label: "Other" },
];

const CHECK_OUTCOME_BY_ITEM_OUTCOME: Record<PsvReviewOutcome, CheckOutcome> = {
  [PsvReviewOutcome.Valid]: CheckOutcome.Valid,
  [PsvReviewOutcome.Issues]: CheckOutcome.ReviewFindings,
  [PsvReviewOutcome.NotAvailable]: CheckOutcome.DataNotAvailable,
};

export function itemCheckOutcome(item: ReportItem): CheckOutcome | null {
  return item.outcome ? CHECK_OUTCOME_BY_ITEM_OUTCOME[item.outcome] : null;
}

/** Still waiting for a staff decision. */
export function isOpenItem(item: ReportItem): boolean {
  const status = item.decision?.status;
  return (
    status === PsvReviewStatus.Pending || status === PsvReviewStatus.InReview
  );
}

/** Open and the sources agree it is valid: safe for one-click acceptance. */
export function isOpenValidItem(item: ReportItem): boolean {
  return item.outcome === PsvReviewOutcome.Valid && isOpenItem(item);
}

/** Issues and unavailable data open by default so they are not skipped; valid
 * items stay collapsed because most are accepted without a closer look. */
export function isExpandedByDefault(item: ReportItem): boolean {
  return (
    isOpenItem(item) &&
    item.outcome !== null &&
    item.outcome !== PsvReviewOutcome.Valid
  );
}

type ReportCheck = RunReport["checks"][number];

export function findItemCheck({
  item,
  checks,
}: {
  item: ReportItem;
  checks: ReportCheck[];
}): ReportCheck | undefined {
  return checks.find((check) => check.id === item.checkId);
}

/** "California · A-173930" for a license, the result ("DEA AB1234567") for
 * an item covering one result, the authority for a check. */
export function itemTitle({
  item,
  checks,
}: {
  item: ReportItem;
  checks: ReportCheck[];
}): string {
  if (item.kind === PsvReviewItemKind.StateLicense) {
    return licenseTitle(item);
  }
  if (item.resultTitle) {
    return item.resultTitle;
  }
  return findItemCheck({ item, checks })?.authorityName ?? "Source check";
}

function licenseTitle(license: {
  stateCode: string | null;
  licenseNumber: string | null;
}): string {
  const state = license.stateCode
    ? (findStateName(license.stateCode) ?? license.stateCode)
    : "State";
  return license.licenseNumber
    ? `${state} · ${license.licenseNumber}`
    : `${state} license`;
}

/** The name of an item staff marked as a concern, as the report titles it. */
export function concernName(concern: ItemSummary["concerns"][number]): string {
  return concern.sourceName ?? licenseTitle(concern);
}

export function concernCount(count: number): string {
  return `${count} ${count === 1 ? "concern" : "concerns"}`;
}

function factText(facts: Record<string, unknown> | undefined, key: string) {
  const value = facts?.[key];
  return typeof value === "string" && value ? value : null;
}

/** Whether the item's details open with an issue banner stating its verdict:
 * every finished item the sources did not find valid. */
export function showsIssueCallout(
  item: ReportItem,
): item is ReportItem & { verdict: NonNullable<ReportItem["verdict"]> } {
  return (
    Boolean(item.verdict) &&
    item.outcome !== null &&
    item.outcome !== PsvReviewOutcome.Valid
  );
}

type ReportResult = NonNullable<ReportCheck["results"]>[number];

/** A license item's deciding source check and its saved result; null for
 * other items and for a license whose deciding source returned no result. */
export function findDecidingResult({
  item,
  checks,
}: {
  item: ReportItem;
  checks: ReportCheck[];
}): { check: ReportCheck; result: ReportResult } | null {
  const source = item.license?.sources.find((entry) => entry.decides);
  if (!source?.resultKey) {
    return null;
  }
  const check = checks.find((entry) => entry.id === source.checkId);
  const result = check?.results?.find(
    (entry) => entry.resultKey === source.resultKey,
  );
  if (!check || !result) {
    return null;
  }
  return { check, result };
}

/** A license's class and expiry, from the deciding source's saved result;
 * null when it has neither, so the row shows the verdict description. */
export function licenseSubtitle({
  item,
  checks,
}: {
  item: ReportItem;
  checks: ReportCheck[];
}): string | null {
  if (item.kind !== PsvReviewItemKind.StateLicense) {
    return null;
  }
  const facts = findDecidingResult({ item, checks })?.result.facts as
    | Record<string, unknown>
    | undefined;
  const licenseClass = factText(facts, "licenseClass");
  const expires =
    factText(facts, "registrationExpiresAt") ?? factText(facts, "expiresAt");
  const parts = [licenseClass, expires ? `Exp. ${formatDate(expires)}` : null];
  return parts.filter(Boolean).join(" · ") || null;
}

const RETRY_WAIT_STATUS = "retry-wait";

/** A source waiting for its next automatic attempt. */
export function isRetryWaiting(check: RunReport["checks"][number]) {
  return check.status === RETRY_WAIT_STATUS;
}

/** The limitations a check lists in its details, without those the
 * disciplinary section already shows. */
export function checkDetailLimitations(check: RunReport["checks"][number]) {
  return check.assessment.items.map((item) => ({
    resultKey: item.resultKey,
    title: item.title,
    limitations: item.limitations.filter(
      (limitation) => !item.disciplinary?.limitations.includes(limitation),
    ),
  }));
}

export const STAFF_STATUS_LABEL: Record<PsvReviewStatus, string> = {
  [PsvReviewStatus.Pending]: "Pending",
  [PsvReviewStatus.Accepted]: "Accepted",
  [PsvReviewStatus.Concern]: "Concern",
  [PsvReviewStatus.InReview]: "In review",
};

/** "Accepted by Sam Staff · Oct 14, 2026, 3:52 PM" for a decided item. A
 * carried decision keeps its original decider and time and names the run it
 * came from. */
export function decisionAttribution(
  decision: NonNullable<ReportItem["decision"]>,
): string {
  const label = STAFF_STATUS_LABEL[decision.status];
  const who = decision.decidedBy?.name;
  const parts = [who ? `${label} by ${who}` : label];
  if (decision.decidedAt) {
    parts.push(formatCredentialTimestamp(decision.decidedAt));
  }
  if (decision.carriedFrom) {
    const runDate = Temporal.Instant.from(
      decision.carriedFrom.runCreatedAt,
    ).toLocaleString(undefined, { dateStyle: "medium" });
    parts.push(`carried forward from the ${runDate} run, matches unchanged`);
  }
  return parts.join(" · ");
}
